import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Row, Col, Input, Table } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";

// Captura por proporciones: en vez de teclear pieza por pieza cada color y talla,
// se captura un TOTAL, que colores entran (con su peso) y la razon por talla
// (CH-M-G-EG en 1-2-2-1). De ahi salen los renglones ya con su cantidad.
//
// El reparto usa restos mayores: primero la parte entera de cada celda y el
// sobrante se va a las celdas con mayor fraccion, de modo que la suma da EXACTO
// el total capturado y no queda desfasado por redondeos.
export function reparte(total, colores, tallas) {
  const sumaC = colores.reduce((s, c) => s + (parseFloat(c.peso) || 0), 0);
  const sumaT = tallas.reduce((s, t) => s + (parseFloat(t.peso) || 0), 0);
  if (!(total > 0) || sumaC <= 0 || sumaT <= 0) return [];

  const celdas = [];
  colores.forEach((c) => {
    tallas.forEach((t) => {
      const exacto = (total * (parseFloat(c.peso) || 0) * (parseFloat(t.peso) || 0)) / (sumaC * sumaT);
      celdas.push({ colores: c._id, tallas: t._id, colorNombre: c.nombre, tallaNombre: t.nombre, exacto });
    });
  });

  let asignado = 0;
  celdas.forEach((c) => { c.cantidad = Math.floor(c.exacto); asignado += c.cantidad; });
  let resto = Math.round(total) - asignado;
  celdas
    .map((c, i) => ({ i, frac: c.exacto - Math.floor(c.exacto) }))
    .sort((a, b) => b.frac - a.frac)
    .slice(0, Math.max(0, resto))
    .forEach(({ i }) => { celdas[i].cantidad += 1; });

  return celdas.filter((c) => c.cantidad > 0);
}

// onNuevoColor / onNuevaTalla: si vienen, el modal deja dar de alta un color o una
// talla ahi mismo y le avisa al padre para que la sume a su reja y a su catalogo.
// catalogoColores / catalogoTallas: catalogo COMPLETO (coloresCat viene acotado al
// color en turno). Sirve para que "agregar" primero busque uno existente y solo
// de alta si no lo encuentra.
// marcarTodos: los colores y tallas que llegan son los del modelo (no el catalogo
// completo), asi que se abren ya marcados y basta escribir total y proporciones.
function ProporcionModal({ abierto, cerrar, coloresCat, tallasCat, onAplicar, titulo, onNuevoColor, onNuevaTalla, catalogoColores, catalogoTallas, marcarTodos }) {
  const [total, setTotal] = useState(0);
  const [colores, setColores] = useState([]);
  const [tallas, setTallas] = useState([]);
  const [nuevoColorNombre, setNuevoColorNombre] = useState("");
  const [nuevaTallaNombre, setNuevaTallaNombre] = useState("");
  const [creando, setCreando] = useState(false);
  // proporcion escrita como en el layout: "2-1-1" para colores, "1-2-2-1" para tallas
  const [textoColores, setTextoColores] = useState("");
  const [textoTallas, setTextoTallas] = useState("");
  const headers = { Authorization: `Bearer: ${localStorage.getItem("app_token")}` };

  const norm = (x) => String(x || "").trim().toUpperCase();
  const etiquetaColor = (c) => c.name;
  // Un registro "es" el texto si coincide su nombre, su codigo, "codigo nombre"
  // o cualquiera de sus variantes (alias: como le llaman los clientes).
  const esAlias = (x, texto) => (x.alias || []).some((a) => norm(a) === texto);

  // Agregar color: si lo escrito coincide con uno del catalogo (por nombre o
  // variante) se usa ese; si no existe, se da de alta con ese nombre.
  async function agregaColor() {
    const texto = norm(nuevoColorNombre);
    if (!texto) return;
    const existente = (catalogoColores || []).find((c) => norm(c.name) === texto || esAlias(c, texto));
    if (existente) {
      if (colores.some((x) => String(x._id) === String(existente._id))) {
        setColores((prev) => prev.map((x) => (String(x._id) === String(existente._id) ? { ...x, activo: true } : x)));
      } else {
        setColores((prev) => [...prev, { _id: existente._id, nombre: etiquetaColor(existente), activo: true, peso: 1 }]);
        if (onNuevoColor) onNuevoColor(existente);
      }
      setNuevoColorNombre("");
      return;
    }
    setCreando(true);
    try {
      const r = await axios.post(process.env.REACT_APP_URL_COLORES, { name: texto }, { headers });
      const c = r.data;
      setColores((prev) => [...prev, { _id: c._id, nombre: etiquetaColor(c), activo: true, peso: 1 }]);
      setNuevoColorNombre("");
      if (onNuevoColor) onNuevoColor(c);
    } catch (error) {
      Swal.fire({ icon: "error", title: "No se pudo crear el color", text: `${error.response?.data?.message || error.message}` });
    }
    setCreando(false);
  }

  async function agregaTalla() {
    const name = norm(nuevaTallaNombre);
    if (!name) return;
    const existente = (catalogoTallas || []).find((t) => norm(t.name) === name || esAlias(t, name));
    if (existente) {
      if (tallas.some((x) => String(x._id) === String(existente._id))) {
        setTallas((prev) => prev.map((x) => (String(x._id) === String(existente._id) ? { ...x, activo: true } : x)));
      } else {
        setTallas((prev) => [...prev, { _id: existente._id, nombre: existente.name, activo: true, peso: 1 }]);
        if (onNuevaTalla) onNuevaTalla(existente);
      }
      setNuevaTallaNombre("");
      return;
    }
    setCreando(true);
    try {
      const r = await axios.post(process.env.REACT_APP_URL_TALLAS, { name }, { headers });
      const t = r.data;
      setTallas((prev) => [...prev, { _id: t._id, nombre: t.name, activo: true, peso: 1 }]);
      setNuevaTallaNombre("");
      if (onNuevaTalla) onNuevaTalla(t);
    } catch (error) {
      Swal.fire({ icon: "error", title: "No se pudo crear la talla", text: `${error.response?.data || error.message}` });
    }
    setCreando(false);
  }

  useEffect(() => {
    if (!abierto) return;
    setTotal(0);
    setTextoColores(""); setTextoTallas("");
    // si solo hay uno, se marca aunque no venga marcarTodos
    const marca = (lista) => !!marcarTodos || (lista || []).length === 1;
    setColores((coloresCat || []).map((c) => ({
      _id: c._id, nombre: c.name, activo: marca(coloresCat), peso: 1,
    })));
    setTallas((tallasCat || []).map((t) => ({
      _id: t._id, nombre: t.name, activo: marca(tallasCat), peso: 1,
    })));
    // Solo al abrir: si el padre se vuelve a renderizar (p.ej. al dar de alta un
    // color desde aqui) no se pierde lo que ya estaba marcado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto]);

  const cambia = (lista, setLista, id, campo, valor) =>
    setLista(lista.map((x) => (x._id === id ? { ...x, [campo]: valor } : x)));

  // "2-1-1" como en el layout: los numeros van en el orden de la lista; 0 = no
  // lleva, y los que se quedan sin numero no entran. Las casillas lo reflejan.
  const aplicaTexto = (texto, lista, setLista) => {
    const nums = String(texto || "").split(/[^0-9.]+/).filter(Boolean).map(Number);
    if (!nums.length) return;
    setLista(lista.map((x, i) => (i < nums.length
      ? { ...x, peso: nums[i], activo: nums[i] > 0 }
      : { ...x, activo: false })));
  };

  const colActivos = colores.filter((c) => c.activo && parseFloat(c.peso) > 0);
  const talActivas = tallas.filter((t) => t.activo && parseFloat(t.peso) > 0);
  const previa = reparte(parseFloat(total) || 0, colActivos, talActivas);
  const sumaPrevia = previa.reduce((s, c) => s + c.cantidad, 0);
  const cantPrevia = (idC, idT) => (previa.find((c) => c.colores === idC && c.tallas === idT) || {}).cantidad || 0;
  const sumaFila = (idC) => previa.filter((c) => c.colores === idC).reduce((s, c) => s + c.cantidad, 0);
  const sumaCol = (idT) => previa.filter((c) => c.tallas === idT).reduce((s, c) => s + c.cantidad, 0);
  const fmt = (n) => new Intl.NumberFormat("es-MX").format(n);
  const pasoStyle = { fontSize: "0.72rem", fontWeight: 700, color: "#0f766e", textTransform: "uppercase", letterSpacing: ".04em" };

  const aplicar = () => {
    if (previa.length === 0) return;
    onAplicar(previa);
    cerrar();
  };

  return (
    <Modal size="xl" isOpen={abierto} toggle={cerrar}>
      <ModalHeader toggle={cerrar}>{titulo || "Captura por proporcion"}</ModalHeader>
      <ModalBody>
        <Row className="mb-3">
          <Col md={3}>
            <div style={pasoStyle}>1 · Total de piezas</div>
            <Input type="number" min="0" value={total} onChange={(e) => setTotal(e.target.value)} />
          </Col>
          <Col md={9} style={{ display: "flex", alignItems: "flex-end" }}>
            <p style={{ margin: 0, color: "#64748b", fontSize: "0.82rem" }}>
              Igual que en el layout: escribe el total, cuánto lleva cada color y cuánto cada talla,
              en el orden de la lista (0 = no lleva). Ej.: 1,800 piezas, colores <b>2-1-1</b> (el primero
              lleva el doble), tallas <b>1-2-2-1</b>. El sistema llena la reja y siempre cuadra con el total.
            </p>
          </Col>
        </Row>

        <Row>
          <Col md={3}>
            <div style={pasoStyle}>2 · Proporción de colores</div>
            <Input bsSize="sm" placeholder="Ej. 2-1-1" value={textoColores} className="mb-1"
              onChange={(e) => { const v = e.target.value; setTextoColores(v); aplicaTexto(v, colores, setColores); }} />
            <div style={{ maxHeight: 240, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 6, padding: 8 }}>
              {colores.map((c) => (
                <Row key={c._id} className="align-items-center" style={{ marginBottom: 4 }}>
                  <Col xs={2}>
                    <Input type="checkbox" style={{ position: "static", margin: 0 }} checked={c.activo}
                      onChange={(e) => cambia(colores, setColores, c._id, "activo", e.target.checked)} />
                  </Col>
                  <Col xs={6}><span style={{ fontSize: "0.82rem" }}>{c.nombre}</span></Col>
                  <Col xs={4} title="Cuanto lleva este color (proporcion)">
                    <Input bsSize="sm" type="number" min="0" step="any" value={c.peso} disabled={!c.activo}
                      onChange={(e) => cambia(colores, setColores, c._id, "peso", e.target.value)} />
                  </Col>
                </Row>
              ))}
            </div>
            {onNuevoColor ? (
              <div style={{ display: "flex", gap: 4, marginTop: 6 }}>
                <Input bsSize="sm" placeholder="Color existente o nuevo" list="prop-lista-colores" value={nuevoColorNombre}
                  onChange={(e) => setNuevoColorNombre(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); agregaColor(); } }} />
                <datalist id="prop-lista-colores">
                  {(catalogoColores || []).map((c) => [
                    <option key={c._id} value={etiquetaColor(c)} />,
                    ...(c.alias || []).map((a) => <option key={`${c._id}-${a}`} value={a}>{`= ${etiquetaColor(c)}`}</option>),
                  ])}
                </datalist>
                <Button size="sm" color="success" disabled={creando} onClick={agregaColor} title="Agregar color (existente o nuevo)">
                  <i className="fas fa-plus" />
                </Button>
              </div>
            ) : null}
          </Col>

          <Col md={3}>
            <div style={pasoStyle}>3 · Proporción de tallas</div>
            <Input bsSize="sm" placeholder="Ej. 1-2-2-1" value={textoTallas} className="mb-1"
              onChange={(e) => { const v = e.target.value; setTextoTallas(v); aplicaTexto(v, tallas, setTallas); }} />
            <div style={{ maxHeight: 240, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 6, padding: 8 }}>
              {tallas.map((t) => (
                <Row key={t._id} className="align-items-center" style={{ marginBottom: 4 }}>
                  <Col xs={3}>
                    <Input type="checkbox" style={{ position: "static", margin: 0 }} checked={t.activo}
                      onChange={(e) => cambia(tallas, setTallas, t._id, "activo", e.target.checked)} />
                  </Col>
                  <Col xs={5}><span style={{ fontSize: "0.82rem" }}>{t.nombre}</span></Col>
                  <Col xs={4} title="Cuanto lleva esta talla (proporcion)">
                    <Input bsSize="sm" type="number" min="0" step="any" value={t.peso} disabled={!t.activo}
                      onChange={(e) => cambia(tallas, setTallas, t._id, "peso", e.target.value)} />
                  </Col>
                </Row>
              ))}
            </div>
            {onNuevaTalla ? (
              <div style={{ display: "flex", gap: 4, marginTop: 6 }}>
                <Input bsSize="sm" placeholder="Talla existente o nueva" list="prop-lista-tallas" value={nuevaTallaNombre}
                  onChange={(e) => setNuevaTallaNombre(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); agregaTalla(); } }} />
                <datalist id="prop-lista-tallas">
                  {(catalogoTallas || []).map((t) => [
                    <option key={t._id} value={t.name} />,
                    ...(t.alias || []).map((a) => <option key={`${t._id}-${a}`} value={a}>{`= ${t.name}`}</option>),
                  ])}
                </datalist>
                <Button size="sm" color="success" disabled={creando} onClick={agregaTalla} title="Agregar talla (existente o nueva)">
                  <i className="fas fa-plus" />
                </Button>
              </div>
            ) : null}
          </Col>

          <Col md={6}>
            <div style={pasoStyle}>Así queda la reja</div>
            <div style={{ maxHeight: 280, overflow: "auto", border: "1px solid #e2e8f0", borderRadius: 6 }}>
              {previa.length ? (
                <Table size="sm" style={{ marginBottom: 0, fontSize: "0.8rem" }}>
                  <thead>
                    <tr>
                      <th>Color</th>
                      {talActivas.map((t) => <th key={t._id} style={{ textAlign: "right" }}>{t.nombre}</th>)}
                      <th style={{ textAlign: "right" }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {colActivos.map((c) => (
                      <tr key={c._id}>
                        <td>{c.nombre}</td>
                        {talActivas.map((t) => <td key={t._id} style={{ textAlign: "right" }}>{fmt(cantPrevia(c._id, t._id))}</td>)}
                        <td style={{ textAlign: "right", fontWeight: 700 }}>{fmt(sumaFila(c._id))}</td>
                      </tr>
                    ))}
                    <tr style={{ background: "#f8fafc", fontWeight: 700 }}>
                      <td>Total</td>
                      {talActivas.map((t) => <td key={t._id} style={{ textAlign: "right" }}>{fmt(sumaCol(t._id))}</td>)}
                      <td style={{ textAlign: "right" }}>{fmt(sumaPrevia)}</td>
                    </tr>
                  </tbody>
                </Table>
              ) : (
                <p style={{ margin: 12, textAlign: "center", color: "#94a3b8" }}>Escribe el total y marca al menos un color y una talla</p>
              )}
            </div>
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color="success" onClick={aplicar} disabled={previa.length === 0}>
          Aplicar {previa.length ? `(${fmt(sumaPrevia)} piezas)` : ""}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default ProporcionModal;
